import React, { useCallback, useEffect, useState, useMemo } from 'react';
import { PropTypes } from 'prop-types';
import { push } from 'connected-react-router';
import { useDispatch } from 'react-redux';
import Joi from 'joi';

import { texts } from 'utils/languages/fr';
import {
    useCheckEmailAvailabilityMutation,
    useCreateReferralMutation,
    useGetInvestmentFundsQuery,
    useGetShareCategoriesQuery,
    useGetShareSubcategoriesQuery,
} from 'api/main';
import { useDebounce } from 'utils/hooks';
import { validateSchema, createCategoryLookup } from 'utils/helpers';
import { EMAIL_VALIDATION_OPTIONS, emailAvailabilityStatus, NAME_REGEX } from 'utils/constants';
import { titles } from 'utils/formData';

import {
    CustomTextField,
    CustomSelect,
    ErrorMessage,
    Loader,
    CustomDialogTitle,
    CancelButton,
    ActionButton,
} from 'components';
import { MenuItem, InputAdornment, Dialog, DialogContent, DialogActions } from '@mui/material';

const renderOption = opt => (
    <MenuItem key={opt.id} value={opt.id}>
        {opt.name}
    </MenuItem>
);

// Find category for a subcategory using the lookup map
const findCategoryForSubcategory = (subcategoryId, categoryLookup) => {
    return categoryLookup.get(subcategoryId);
};

// Filter subcategories by category criteria
const filterSubcategoriesByCategory = (subcategories, categoryLookup, filterFn) => {
    return subcategories.filter(subcategory => {
        const category = findCategoryForSubcategory(subcategory.id, categoryLookup);
        return category && filterFn(subcategory, category);
    });
};

const getUniqueSubcategories = (subcategories, categoryLookup) => {
    const categoryMap = subcategories.reduce((acc, item) => {
        const category = findCategoryForSubcategory(item.id, categoryLookup);
        if (category && !acc[category.id]) {
            acc[category.id] = item; // pick the first subcategory found
        }
        return acc;
    }, {});

    return Object.values(categoryMap);
};

const getSubcategoryLabel = (subcategory, category) => {
    const privateLabel = subcategory.accessibleToDistribution
        ? texts.CLEAN_SHARE + ` (${category.name})`
        : texts.RESERVED;

    return category?.private ? privateLabel : texts.NORMAL + ` (${category.name})`;
};

const renderSubcategoryOption = (subcategory, category, list, categoryLookup) => {
    let privateLabel = subcategory.accessibleToDistribution
        ? texts.CLEAN_SHARE + ` (${category.name})`
        : texts.RESERVED;

    const hasMatchingSibling = list.find(item => {
        const itemCategory = findCategoryForSubcategory(item.id, categoryLookup);
        return (
            item.id !== subcategory.id &&
            getSubcategoryLabel(subcategory, category) === getSubcategoryLabel(item, itemCategory)
        );
    });

    if (hasMatchingSibling) {
        privateLabel += ` (${subcategory.name})`;
    }

    return (
        <MenuItem key={subcategory.id} value={subcategory.id}>
            {category.private ? privateLabel : texts.NORMAL + ` (${category.name})`}
        </MenuItem>
    );
};

// Helper function to render a group of subcategories
const renderSubcategoryGroup = (subcategories, categoryLookup) => {
    return subcategories?.map(sc => {
        const category = findCategoryForSubcategory(sc.id, categoryLookup);
        return renderSubcategoryOption(sc, category, subcategories, categoryLookup);
    });
};

const defaultRefferalData = {
    selectedFund: '',
    admissionFee: '',
    selectedSubcategory: '',
    inviteEmail: '',
    title: titles[0],
    firstName: '',
    lastName: '',
};

const SubscriptionInvite = ({ open, handleToggle, user }) => {
    const dispatch = useDispatch();
    const [form, setForm] = useState({
        ...defaultRefferalData,
        inviteEmail: user?.email || '',
    });
    const [errors, setErrors] = useState({
        inviteEmail: '',
        admissionFee: '',
    });
    const debouncedEmail = useDebounce(form.inviteEmail, 500);

    const { data: shareCategories = [], isLoading: categoriesLoader } = useGetShareCategoriesQuery(
        { investmentFund: form.selectedFund },
        { skip: !form.selectedFund }
    );
    const { data: shareSubcategories = [], isLoading: subcategoriesLoader } =
        useGetShareSubcategoriesQuery(
            { 'shareCategory.investmentFund': +form.selectedFund },
            { skip: !form.selectedFund }
        );
    const { data: fundList = [], isFetching: fundsLoader } = useGetInvestmentFundsQuery({
        visible: 1,
    });
    const [createReferral, { isSuccess, isLoading, reset }] = useCreateReferralMutation();
    const [checkEmail, { isLoading: checkLoading, data: checkResponse, reset: checkReset }] =
        useCheckEmailAvailabilityMutation();

    const parsedFundList = useMemo(
        () =>
            fundList
                .filter(fund => fund.visible && fund.enabled)
                .map(fund => ({
                    id: fund.id,
                    name: fund.name,
                    maximumAdmissionFee: fund.maximumAdmissionFee,
                    shareCategories: fund.shareCategories,
                })),
        [fundList]
    );
    // Create category lookup map for efficient category finding
    const categoryLookup = useMemo(() => createCategoryLookup(shareCategories), [shareCategories]);

    const normalSubcategories = useMemo(() => {
        const filteredSubcategories = filterSubcategoriesByCategory(
            shareSubcategories,
            categoryLookup,
            (subcategory, category) => !category.private
        );
        return getUniqueSubcategories(filteredSubcategories, categoryLookup);
    }, [shareSubcategories, categoryLookup]);

    const cleanSubcategories = useMemo(() => {
        const filteredSubcategories = filterSubcategoriesByCategory(
            shareSubcategories,
            categoryLookup,
            (subcategory, category) => category.private && subcategory.accessibleToDistribution
        );
        return getUniqueSubcategories(filteredSubcategories, categoryLookup);
    }, [shareSubcategories, categoryLookup]);

    const reservedSubcategories = useMemo(() => {
        return filterSubcategoriesByCategory(
            shareSubcategories,
            categoryLookup,
            (subcategory, category) => category.private && !subcategory.accessibleToDistribution
        );
    }, [shareSubcategories, categoryLookup]);

    useEffect(() => {
        if (!open) {
            setForm(defaultRefferalData);
            reset();
        }
    }, [open, reset]);

    useEffect(() => {
        if (open) {
            setForm(prevState => ({ ...prevState, inviteEmail: user?.email || '' }));
        }
    }, [user, open]);

    const { selectedFund, selectedSubcategory, admissionFee, inviteEmail } = form;

    const handleRefferalInputs = useCallback(
        ({ target: { value, name } }) => {
            if (name === 'inviteEmail') {
                checkReset();
            }

            setForm(prevState => ({
                ...prevState,
                [name]:
                    name !== 'selectedFund' &&
                    name !== 'selectedSubcategory' &&
                    name !== 'firstName' &&
                    name !== 'lastName'
                        ? value.trim()
                        : value,
                ...(name === 'selectedFund' && { selectedSubcategory: '' }),
            }));
            setErrors({ inviteEmail: '', admissionFee: '' });
        },
        [checkReset]
    );

    const maxAdmissionFee = useMemo(() => {
        const fund = parsedFundList.find(f => f.id === +selectedFund);
        return fund?.maximumAdmissionFee || 0;
    }, [selectedFund, parsedFundList]);

    const schema = useMemo(
        () => ({
            admissionFee: Joi.number().required().max(maxAdmissionFee).messages({
                'number.empty': texts.WRONG_ADMISSION_FEE,
                'number.max': texts.WRONG_ADMISSION_FEE,
            }),
            inviteEmail: Joi.string().required().email(EMAIL_VALIDATION_OPTIONS).messages({
                'string.empty': texts.SAISIR_LE_CHAMP,
                'string.email': texts.ADRESSE_EMAIL_SAISIE_PAS_VALIDE,
            }),
            firstName: Joi.string().required().trim().min(2).max(120).pattern(NAME_REGEX).messages({
                'string.empty': texts.SAISIR_LE_CHAMP,
                'string.min': texts.FIRST_NAME_LENGTH,
                'string.max': texts.FIRST_NAME_LENGTH,
                'string.pattern.base': texts.FIRST_NAME_PATTERN,
            }),
            lastName: Joi.string().required().trim().min(2).max(120).pattern(NAME_REGEX).messages({
                'string.empty': texts.SAISIR_LE_CHAMP,
                'string.min': texts.LAST_NAME_LENGTH,
                'string.max': texts.LAST_NAME_LENGTH,
                'string.pattern.base': texts.LAST_NAME_PATTERN,
            }),
        }),
        [maxAdmissionFee]
    );

    const emailIsValid = useMemo(() => {
        const { valid } = validateSchema(schema.inviteEmail, debouncedEmail);
        return valid;
    }, [schema, debouncedEmail]);

    const emailExists = useMemo(
        () =>
            !!checkResponse?.status && checkResponse?.status !== emailAvailabilityStatus.AVAILABLE,
        [checkResponse]
    );
    const emailEligible = useMemo(
        () =>
            checkResponse?.status === emailAvailabilityStatus.AVAILABLE ||
            checkResponse?.status === emailAvailabilityStatus.WITH_FINISHED_SUBSCRIPTION,
        [checkResponse]
    );

    useEffect(() => {
        if (emailIsValid) {
            checkEmail(debouncedEmail);
        }
    }, [emailIsValid, debouncedEmail, checkEmail]);

    useEffect(() => {
        if (checkResponse?.userId && emailExists && emailIsValid) {
            setForm(prevState => ({
                ...prevState,
                firstName: checkResponse.firstName,
                lastName: checkResponse.lastName,
                title: checkResponse.title,
            }));
        } else {
            setForm(prevState => ({
                ...prevState,
                firstName: '',
                lastName: '',
                title: titles[0],
            }));
        }
    }, [checkResponse, emailExists, emailIsValid]);

    const formIsValid = useCallback(() => {
        const { valid, newErrors } = validateSchema(schema, form);
        setErrors(newErrors);
        return valid;
    }, [form, schema]);

    const handleSendReferral = useCallback(() => {
        if (formIsValid()) {
            const payload = {
                shareSubcategoryId: +selectedSubcategory,
                admissionFee: parseFloat(admissionFee),
                email: form.inviteEmail,
            };
            payload.firstName = form.firstName;
            payload.lastName = form.lastName;
            payload.title = form.title;
            createReferral(payload);
        }
    }, [formIsValid, admissionFee, selectedSubcategory, form, createReferral]);

    // side effect for createReferral
    useEffect(() => {
        if (isSuccess) {
            reset();
            handleToggle();
            dispatch(push('/subscriptions'));
        }
    }, [isSuccess, handleToggle, reset, dispatch]);

    const allowInvite = useMemo(() => {
        return (
            !!selectedFund &&
            !!inviteEmail &&
            !!selectedSubcategory &&
            emailEligible &&
            !isNaN(parseFloat(admissionFee))
        );
    }, [selectedFund, selectedSubcategory, inviteEmail, admissionFee, emailEligible]);

    const userFieldsDisabled = checkLoading || emailExists;

    return (
        <Dialog open={open}>
            <CustomDialogTitle
                handleClose={handleToggle}
                title={texts.CREATE_SUBSCRIPTION}
                disableBorder
            />
            <DialogContent>
                <Loader loading={fundsLoader}>
                    <CustomTextField
                        label={texts.E_MAIL}
                        disabled={!!user?.email}
                        onChange={handleRefferalInputs}
                        value={inviteEmail}
                        name="inviteEmail"
                        error={errors.inviteEmail}
                    />
                    <CustomSelect
                        disabled={userFieldsDisabled}
                        value={form.title}
                        onChange={handleRefferalInputs}
                        name="title"
                        label={texts.CIVILITE}
                    >
                        {titles.map(t => (
                            <MenuItem value={t} key={t}>
                                {t}
                            </MenuItem>
                        ))}
                    </CustomSelect>
                    <CustomTextField
                        disabled={userFieldsDisabled}
                        name="firstName"
                        label={texts.PRENOM}
                        value={form.firstName}
                        error={errors.firstName}
                        onChange={handleRefferalInputs}
                    />
                    <CustomTextField
                        disabled={userFieldsDisabled}
                        name="lastName"
                        label={texts.NOM}
                        value={form.lastName}
                        error={errors.lastName}
                        onChange={handleRefferalInputs}
                    />
                    <CustomSelect
                        label={texts.PRODUIT}
                        onChange={handleRefferalInputs}
                        value={selectedFund}
                        error={errors.selectedFund}
                        name="selectedFund"
                    >
                        {parsedFundList?.map(renderOption)}
                    </CustomSelect>
                    <CustomSelect
                        label={texts.CATEGORIE_DE_PARTS}
                        onChange={handleRefferalInputs}
                        value={selectedSubcategory}
                        name="selectedSubcategory"
                        error={errors.selectedSubcategory}
                        disabled={
                            !selectedFund ||
                            !shareSubcategories[0] ||
                            categoriesLoader ||
                            subcategoriesLoader
                        }
                    >
                        {renderSubcategoryGroup(normalSubcategories, categoryLookup)}
                        {renderSubcategoryGroup(cleanSubcategories, categoryLookup)}
                        {renderSubcategoryGroup(reservedSubcategories, categoryLookup)}
                    </CustomSelect>
                    <CustomTextField
                        formatNumber
                        name="admissionFee"
                        label={texts.ADMISSION_FEE}
                        value={admissionFee}
                        placeholder={`Max ${maxAdmissionFee}%`}
                        onChange={handleRefferalInputs}
                        error={errors.admissionFee}
                        endAdornment={<InputAdornment position="start">%</InputAdornment>}
                    />
                    {checkResponse?.status === emailAvailabilityStatus.WITH_ONGOING_SUBSCRIPTION &&
                        form.inviteEmail && (
                            <ErrorMessage
                                align="center"
                                message={texts.SUBSCRIPTION_ALREADY_IN_PROGRESS}
                            />
                        )}
                    {checkResponse?.status === emailAvailabilityStatus.NOT_INVESTOR &&
                        form.inviteEmail && (
                            <ErrorMessage align="center" message={texts.ACCOUNT_INELIGIBLE} />
                        )}
                </Loader>
            </DialogContent>
            <DialogActions>
                <CancelButton disabled={isLoading} onClick={handleToggle}>
                    {texts.CANCEL}
                </CancelButton>
                <ActionButton
                    size="small"
                    loading={isLoading}
                    onClick={handleSendReferral}
                    disabled={!allowInvite || isLoading || checkLoading}
                >
                    {texts.ENVOYER}
                </ActionButton>
            </DialogActions>
        </Dialog>
    );
};

SubscriptionInvite.propTypes = {
    open: PropTypes.bool,
    handleToggle: PropTypes.func.isRequired,
    user: PropTypes.object,
};

export default SubscriptionInvite;
